import { API, Endpoints, Lang, Project, ProjectAPI, Team, TeamAPI } from '@brg/api'
import { OrganizationsOrderGetListParams } from './buildOrganizationsOrderModalGetListParams'
import { isOrganizationsEntityType } from './prepareCustomSortingGridFilter'

type GlobalProject = Project<'Global'>
type GlobalTeam = Team<'Global'>
type TeamWithServiceFlag = GlobalTeam & { isServiceTeam?: boolean }

const isServiceOrganizationTeam = (team: TeamWithServiceFlag): boolean => team.isServiceTeam === true

const excludeServiceOrganizationTeams = <T extends TeamWithServiceFlag>(teams: T[]): T[] => (
  teams.filter((team) => !isServiceOrganizationTeam(team))
)

export const TEAM_TAB_PROJECTS_ORDER_PAGE_LIMIT = 12

export type TeamTabProjectsPagePaging = {
  page: number
  pages: number
  count: number
  offset: number
}

export type TeamTabProjectsPageResult = {
  projects: Project[]
  paging: TeamTabProjectsPagePaging
}

export type OrganizationsListSource = 'contexts' | 'myAdminTeams'

type FetchTeamTabProjectsPageParams = {
  contextNames?: string[]
  teamId?: string
  organizationsGetListParams?: OrganizationsOrderGetListParams
  organizationsListSource?: OrganizationsListSource
  entityType?: string
  language?: string
  limit?: number
  offset?: number
}

type FetchTeamTabProjectsParams = {
  contextNames?: string[]
  teamId?: string
  organizationsGetListParams?: OrganizationsOrderGetListParams
  organizationsListSource?: OrganizationsListSource
  entityType?: string
  projectIds?: string[]
  language?: string
}

const emptyPageResult = (offset = 0): TeamTabProjectsPageResult => ({
  projects: [],
  paging: { page: 0, pages: 0, count: 0, offset },
})

const sortItemsByIds = <T extends { projectId: string }>(items: T[], ids: string[]): T[] => {
  const map = new Map(items.map((item) => [item.projectId, item]))
  return ids.map((id) => map.get(id)).filter(Boolean) as T[]
}

const localizeProjects = (projects: GlobalProject[], language?: string): Project[] => {
  if (language) {
    return API.lang(projects, language) as Project[]
  }
  return projects as unknown as Project[]
}

const mapTeamToOrderCard = (team: GlobalTeam, language?: string): Project => {
  const localizedTeam = language
    ? API.lang([team], language)[0] as GlobalTeam
    : team

  const title = typeof localizedTeam.name === 'string'
    ? localizedTeam.name
    : (Array.isArray(localizedTeam.name)
      ? Lang(localizedTeam.name, language || 'ru')
      : localizedTeam.displayName || '')

  return {
    ...(localizedTeam as object),
    projectId: localizedTeam.teamId,
    title,
  } as Project
}

const localizeTeamsAsProjects = (teams: GlobalTeam[], language?: string): Project[] => (
  teams.map((team) => mapTeamToOrderCard(team, language))
)

const parsePageResponse = (page: any, offset: number) => {
  const data = Array.isArray(page?.data) ? page.data : []
  const paging = page?.paging || {
    page: data.length ? 1 : 0,
    pages: data.length ? 1 : 0,
    count: data.length,
    offset,
  }

  return { data, paging }
}

const fetchProjectsPageFromGetList = async ({
  contextNames = [],
  teamId,
  limit,
  offset,
}: FetchTeamTabProjectsPageParams) => {
  const hasContexts = contextNames.length > 0
  const hasTeamId = !!teamId

  const page = await API.Json(
    ProjectAPI.GetList({
      ...(hasContexts ? { contexts: contextNames } : {}),
      ...(hasTeamId && !hasContexts ? {
        authorIdsFromTeamIds: [teamId],
        isContextIgnored: true,
      } : {}),
      projectStatus: [1, 10],
      projectFilter: 0,
      limit,
      offset,
      isLite: false,
      mainPage: false,
    }),
  )

  return parsePageResponse(page, offset ?? 0)
}

const fetchMyAdminTeamsAll = async (): Promise<GlobalTeam[]> => {
  const response = await API.request()
    .endpoint(Endpoints.geograffee)
    .url('/api/Team/GetMyAdminTeams')
    .get()
    .send()

  const json = await response.json()
  if (Array.isArray(json)) {
    return json as GlobalTeam[]
  }

  return (Array.isArray(json?.data) ? json.data : []) as GlobalTeam[]
}

const fetchMyAdminTeamsPage = async ({
  limit = TEAM_TAB_PROJECTS_ORDER_PAGE_LIMIT,
  offset = 0,
}: FetchTeamTabProjectsPageParams) => {
  const allTeams = excludeServiceOrganizationTeams(await fetchMyAdminTeamsAll())
  const slice = allTeams.slice(offset, offset + limit)

  return {
    data: slice,
    paging: {
      page: limit > 0 ? Math.floor(offset / limit) + 1 : 1,
      pages: limit > 0 ? Math.max(1, Math.ceil(allTeams.length / limit)) : 1,
      count: allTeams.length,
      offset,
    },
  }
}

const fetchOrganizationsPageFromGetList = async ({
  organizationsGetListParams,
  organizationsListSource,
  limit = TEAM_TAB_PROJECTS_ORDER_PAGE_LIMIT,
  offset = 0,
}: FetchTeamTabProjectsPageParams) => {
  if (organizationsListSource === 'myAdminTeams') {
    return fetchMyAdminTeamsPage({ limit, offset })
  }

  const page = await API.Json(
    TeamAPI.GetList({
      contexts: organizationsGetListParams!.contexts,
      excludeModerationTypes: organizationsGetListParams!.excludeModerationTypes,
      isServiceTeam: organizationsGetListParams!.isServiceTeam,
      limit,
      page: Math.floor(offset / limit) + 1,
    }),
  ) as any

  const parsed = parsePageResponse(page, offset ?? 0)
  return {
    ...parsed,
    data: excludeServiceOrganizationTeams(parsed.data as GlobalTeam[]),
  }
}

export const fetchTeamTabProjectsPageForOrder = async ({
  contextNames,
  teamId,
  organizationsGetListParams,
  organizationsListSource,
  entityType,
  language,
  limit = TEAM_TAB_PROJECTS_ORDER_PAGE_LIMIT,
  offset = 0,
}: FetchTeamTabProjectsPageParams): Promise<TeamTabProjectsPageResult> => {
  const isOrganizations = isOrganizationsEntityType(entityType)

  if (isOrganizations) {
    const hasMyAdminTeamsSource = organizationsListSource === 'myAdminTeams'
    if (!hasMyAdminTeamsSource && !organizationsGetListParams?.contexts?.length) {
      return emptyPageResult(offset)
    }
  } else if (!contextNames?.length && !teamId) {
    return emptyPageResult(offset)
  }

  try {
    const pageResult = isOrganizations
      ? await fetchOrganizationsPageFromGetList({
        organizationsGetListParams,
        organizationsListSource,
        limit,
        offset,
      })
      : await fetchProjectsPageFromGetList({ contextNames, teamId, limit, offset })

    const projects = isOrganizations
      ? localizeTeamsAsProjects(pageResult.data as GlobalTeam[], language)
      : localizeProjects(pageResult.data as GlobalProject[], language)

    return {
      projects,
      paging: pageResult.paging,
    }
  } catch {
    return emptyPageResult(offset)
  }
}

const fetchProjectsByIdsFromGetList = async ({
  contextNames = [],
  teamId,
  projectIds,
}: {
  contextNames: string[]
  teamId?: string
  projectIds: string[]
}) => {
  const hasContexts = contextNames.length > 0

  const page = await API.Json(
    ProjectAPI.GetList({
      projectIds,
      ...(hasContexts ? { contexts: contextNames } : {}),
      ...(teamId && !hasContexts ? { authorIdsFromTeamIds: [teamId] } : {}),
      projectStatus: [1, 10],
      projectFilter: 0,
      isLite: false,
      isContextIgnored: true,
      orderByProjectIds: true,
      mainPage: false,
    }),
  )

  return (Array.isArray(page?.data) ? page.data : []) as GlobalProject[]
}

const fetchOrganizationsByIdsFromGetList = async ({
  organizationsGetListParams,
  projectIds: teamIds,
}: {
  organizationsGetListParams: OrganizationsOrderGetListParams
  projectIds: string[]
}) => {
  const page = await API.Json(
    TeamAPI.GetList({
      contexts: organizationsGetListParams.contexts,
      excludeModerationTypes: organizationsGetListParams.excludeModerationTypes,
      isServiceTeam: organizationsGetListParams.isServiceTeam,
      teamIds,
      isContextIgnored: true,
      orderByTeamIds: true,
    }),
  ) as any

  return excludeServiceOrganizationTeams(
    (Array.isArray(page?.data) ? page.data : []) as GlobalTeam[],
  )
}

const fetchProjectsByIdsFromGet = async (projectIds: string[]): Promise<GlobalProject[]> => {
  const byIds = await Promise.all(
    projectIds.map(async (id) => {
      try {
        return await API.Json(ProjectAPI.Get({ idProject: id }))
      } catch {
        return null
      }
    }),
  )

  return byIds.filter((project): project is GlobalProject => project != null)
}

const fetchOrganizationsByIdsFromGet = async (teamIds: string[]): Promise<GlobalTeam[]> => {
  const byIds = await Promise.all(
    teamIds.map(async (id) => {
      try {
        return await API.Json(TeamAPI.Get({ idTeam: id }))
      } catch {
        return null
      }
    }),
  )

  return byIds.filter((team): team is GlobalTeam => team != null && !isServiceOrganizationTeam(team))
}

const fetchMyAdminTeamsByIds = async (teamIds: string[]): Promise<GlobalTeam[]> => {
  const allTeams = excludeServiceOrganizationTeams(await fetchMyAdminTeamsAll())
  const idsSet = new Set(teamIds)
  return allTeams.filter((team) => idsSet.has(team.teamId))
}

export const fetchTeamTabProjectsForOrder = async ({
  contextNames,
  teamId,
  organizationsGetListParams,
  organizationsListSource,
  entityType,
  projectIds = [],
  language,
}: FetchTeamTabProjectsParams): Promise<Project[]> => {
  const ids = (projectIds || []).filter(Boolean)
  if (!ids.length) return []

  const isOrganizations = isOrganizationsEntityType(entityType)

  if (isOrganizations) {
    const hasMyAdminTeamsSource = organizationsListSource === 'myAdminTeams'
    if (!hasMyAdminTeamsSource && !organizationsGetListParams?.contexts?.length) {
      return []
    }
  } else if (!contextNames?.length && !teamId) {
    return []
  }

  let loadedProjects: Project[] = []

  try {
    if (isOrganizations) {
      const loadedTeams = organizationsListSource === 'myAdminTeams'
        ? await fetchMyAdminTeamsByIds(ids)
        : await fetchOrganizationsByIdsFromGetList({
          organizationsGetListParams: organizationsGetListParams!,
          projectIds: ids,
        })
      loadedProjects = localizeTeamsAsProjects(loadedTeams, language)
    } else {
      const loaded = await fetchProjectsByIdsFromGetList({
        contextNames: contextNames || [],
        teamId,
        projectIds: ids,
      })
      loadedProjects = localizeProjects(loaded, language)
    }
  } catch {
    loadedProjects = []
  }

  const loadedIds = new Set(loadedProjects.map((item) => item.projectId))
  const missingIds = ids.filter((id) => !loadedIds.has(id))

  if (missingIds.length) {
    if (isOrganizations) {
      const fromGet = await fetchOrganizationsByIdsFromGet(missingIds)
      loadedProjects = [...loadedProjects, ...localizeTeamsAsProjects(fromGet, language)]
    } else {
      const fromGet = await fetchProjectsByIdsFromGet(missingIds)
      loadedProjects = [...loadedProjects, ...localizeProjects(fromGet, language)]
    }
  }

  return sortItemsByIds(
    excludeServiceOrganizationTeams(loadedProjects as TeamWithServiceFlag[]) as Project[],
    ids,
  )
}
import { API, Endpoints, Lang, Project, ProjectAPI, Team, TeamAPI } from '@brg/api'
import { OrganizationsOrderGetListParams } from './buildOrganizationsOrderModalGetListParams'
import { isOrganizationsEntityType } from './prepareCustomSortingGridFilter'

type GlobalProject = Project<'Global'>
type GlobalTeam = Team<'Global'>
type TeamWithServiceFlag = GlobalTeam & { isServiceTeam?: boolean }

const isServiceOrganizationTeam = (team: TeamWithServiceFlag): boolean => team.isServiceTeam === true

const excludeServiceOrganizationTeams = <T extends TeamWithServiceFlag>(teams: T[]): T[] => (
  teams.filter((team) => !isServiceOrganizationTeam(team))
)

export const TEAM_TAB_PROJECTS_ORDER_PAGE_LIMIT = 12

export type TeamTabProjectsPagePaging = {
  page: number
  pages: number
  count: number
  offset: number
}

export type TeamTabProjectsPageResult = {
  projects: Project[]
  paging: TeamTabProjectsPagePaging
}

export type OrganizationsListSource = 'contexts' | 'myAdminTeams'

type FetchTeamTabProjectsPageParams = {
  contextNames?: string[]
  teamId?: string
  organizationsGetListParams?: OrganizationsOrderGetListParams
  organizationsListSource?: OrganizationsListSource
  entityType?: string
  language?: string
  limit?: number
  offset?: number
}

type FetchTeamTabProjectsParams = {
  contextNames?: string[]
  teamId?: string
  organizationsGetListParams?: OrganizationsOrderGetListParams
  organizationsListSource?: OrganizationsListSource
  entityType?: string
  projectIds?: string[]
  language?: string
}

const emptyPageResult = (offset = 0): TeamTabProjectsPageResult => ({
  projects: [],
  paging: { page: 0, pages: 0, count: 0, offset },
})

const sortItemsByIds = <T extends { projectId: string }>(items: T[], ids: string[]): T[] => {
  const map = new Map(items.map((item) => [item.projectId, item]))
  return ids.map((id) => map.get(id)).filter(Boolean) as T[]
}

const localizeProjects = (projects: GlobalProject[], language?: string): Project[] => {
  if (language) {
    return API.lang(projects, language) as Project[]
  }
  return projects as unknown as Project[]
}

const mapTeamToOrderCard = (team: GlobalTeam, language?: string): Project => {
  const localizedTeam = language
    ? API.lang([team], language)[0] as GlobalTeam
    : team

  const title = typeof localizedTeam.name === 'string'
    ? localizedTeam.name
    : (Array.isArray(localizedTeam.name)
      ? Lang(localizedTeam.name, language || 'ru')
      : localizedTeam.displayName || '')

  return {
    ...(localizedTeam as object),
    projectId: localizedTeam.teamId,
    title,
  } as Project
}

const localizeTeamsAsProjects = (teams: GlobalTeam[], language?: string): Project[] => (
  teams.map((team) => mapTeamToOrderCard(team, language))
)

const parsePageResponse = (page: any, offset: number) => {
  const data = Array.isArray(page?.data) ? page.data : []
  const paging = page?.paging || {
    page: data.length ? 1 : 0,
    pages: data.length ? 1 : 0,
    count: data.length,
    offset,
  }

  return { data, paging }
}

const fetchProjectsPageFromGetList = async ({
  contextNames = [],
  teamId,
  limit,
  offset,
}: FetchTeamTabProjectsPageParams) => {
  const hasContexts = contextNames.length > 0
  const hasTeamId = !!teamId

  const page = await API.Json(
    ProjectAPI.GetList({
      ...(hasContexts ? { contexts: contextNames } : {}),
      ...(hasTeamId && !hasContexts ? {
        authorIdsFromTeamIds: [teamId],
        isContextIgnored: true,
      } : {}),
      projectStatus: [1, 10],
      projectFilter: 0,
      limit,
      offset,
      isLite: false,
      mainPage: false,
    }),
  )

  return parsePageResponse(page, offset ?? 0)
}

const fetchMyAdminTeamsAll = async (): Promise<GlobalTeam[]> => {
  const response = await API.request()
    .endpoint(Endpoints.geograffee)
    .url('/api/Team/GetMyAdminTeams')
    .get()
    .send()

  const json = await response.json()
  if (Array.isArray(json)) {
    return json as GlobalTeam[]
  }

  return (Array.isArray(json?.data) ? json.data : []) as GlobalTeam[]
}

const fetchMyAdminTeamsPage = async ({
  limit = TEAM_TAB_PROJECTS_ORDER_PAGE_LIMIT,
  offset = 0,
}: FetchTeamTabProjectsPageParams) => {
  const allTeams = excludeServiceOrganizationTeams(await fetchMyAdminTeamsAll())
  const slice = allTeams.slice(offset, offset + limit)

  return {
    data: slice,
    paging: {
      page: limit > 0 ? Math.floor(offset / limit) + 1 : 1,
      pages: limit > 0 ? Math.max(1, Math.ceil(allTeams.length / limit)) : 1,
      count: allTeams.length,
      offset,
    },
  }
}

const fetchOrganizationsPageFromGetList = async ({
  organizationsGetListParams,
  organizationsListSource,
  limit = TEAM_TAB_PROJECTS_ORDER_PAGE_LIMIT,
  offset = 0,
}: FetchTeamTabProjectsPageParams) => {
  if (organizationsListSource === 'myAdminTeams') {
    return fetchMyAdminTeamsPage({ limit, offset })
  }

  const page = await API.Json(
    TeamAPI.GetList({
      contexts: organizationsGetListParams!.contexts,
      excludeModerationTypes: organizationsGetListParams!.excludeModerationTypes,
      isServiceTeam: organizationsGetListParams!.isServiceTeam,
      limit,
      page: Math.floor(offset / limit) + 1,
    }),
  ) as any

  const parsed = parsePageResponse(page, offset ?? 0)
  return {
    ...parsed,
    data: excludeServiceOrganizationTeams(parsed.data as GlobalTeam[]),
  }
}

export const fetchTeamTabProjectsPageForOrder = async ({
  contextNames,
  teamId,
  organizationsGetListParams,
  organizationsListSource,
  entityType,
  language,
  limit = TEAM_TAB_PROJECTS_ORDER_PAGE_LIMIT,
  offset = 0,
}: FetchTeamTabProjectsPageParams): Promise<TeamTabProjectsPageResult> => {
  const isOrganizations = isOrganizationsEntityType(entityType)

  if (isOrganizations) {
    const hasMyAdminTeamsSource = organizationsListSource === 'myAdminTeams'
    if (!hasMyAdminTeamsSource && !organizationsGetListParams?.contexts?.length) {
      return emptyPageResult(offset)
    }
  } else if (!contextNames?.length && !teamId) {
    return emptyPageResult(offset)
  }

  try {
    const pageResult = isOrganizations
      ? await fetchOrganizationsPageFromGetList({
        organizationsGetListParams,
        organizationsListSource,
        limit,
        offset,
      })
      : await fetchProjectsPageFromGetList({ contextNames, teamId, limit, offset })

    const projects = isOrganizations
      ? localizeTeamsAsProjects(pageResult.data as GlobalTeam[], language)
      : localizeProjects(pageResult.data as GlobalProject[], language)

    return {
      projects,
      paging: pageResult.paging,
    }
  } catch {
    return emptyPageResult(offset)
  }
}

const fetchProjectsByIdsFromGetList = async ({
  contextNames = [],
  teamId,
  projectIds,
}: {
  contextNames: string[]
  teamId?: string
  projectIds: string[]
}) => {
  const hasContexts = contextNames.length > 0

  const page = await API.Json(
    ProjectAPI.GetList({
      projectIds,
      ...(hasContexts ? { contexts: contextNames } : {}),
      ...(teamId && !hasContexts ? { authorIdsFromTeamIds: [teamId] } : {}),
      projectStatus: [1, 10],
      projectFilter: 0,
      isLite: false,
      isContextIgnored: true,
      orderByProjectIds: true,
      mainPage: false,
    }),
  )

  return (Array.isArray(page?.data) ? page.data : []) as GlobalProject[]
}

const fetchOrganizationsByIdsFromGetList = async ({
  organizationsGetListParams,
  projectIds: teamIds,
}: {
  organizationsGetListParams: OrganizationsOrderGetListParams
  projectIds: string[]
}) => {
  const page = await API.Json(
    TeamAPI.GetList({
      contexts: organizationsGetListParams.contexts,
      excludeModerationTypes: organizationsGetListParams.excludeModerationTypes,
      isServiceTeam: organizationsGetListParams.isServiceTeam,
      teamIds,
      isContextIgnored: true,
      orderByTeamIds: true,
    }),
  ) as any

  return excludeServiceOrganizationTeams(
    (Array.isArray(page?.data) ? page.data : []) as GlobalTeam[],
  )
}

const fetchProjectsByIdsFromGet = async (projectIds: string[]): Promise<GlobalProject[]> => {
  const byIds = await Promise.all(
    projectIds.map(async (id) => {
      try {
        return await API.Json(ProjectAPI.Get({ idProject: id }))
      } catch {
        return null
      }
    }),
  )

  return byIds.filter((project): project is GlobalProject => project != null)
}

const fetchOrganizationsByIdsFromGet = async (teamIds: string[]): Promise<GlobalTeam[]> => {
  const byIds = await Promise.all(
    teamIds.map(async (id) => {
      try {
        return await API.Json(TeamAPI.Get({ idTeam: id }))
      } catch {
        return null
      }
    }),
  )

  return byIds.filter((team): team is GlobalTeam => team != null && !isServiceOrganizationTeam(team))
}

const fetchMyAdminTeamsByIds = async (teamIds: string[]): Promise<GlobalTeam[]> => {
  const allTeams = excludeServiceOrganizationTeams(await fetchMyAdminTeamsAll())
  const idsSet = new Set(teamIds)
  return allTeams.filter((team) => idsSet.has(team.teamId))
}

export const fetchTeamTabProjectsForOrder = async ({
  contextNames,
  teamId,
  organizationsGetListParams,
  organizationsListSource,
  entityType,
  projectIds = [],
  language,
}: FetchTeamTabProjectsParams): Promise<Project[]> => {
  const ids = (projectIds || []).filter(Boolean)
  if (!ids.length) return []

  const isOrganizations = isOrganizationsEntityType(entityType)

  if (isOrganizations) {
    const hasMyAdminTeamsSource = organizationsListSource === 'myAdminTeams'
    if (!hasMyAdminTeamsSource && !organizationsGetListParams?.contexts?.length) {
      return []
    }
  } else if (!contextNames?.length && !teamId) {
    return []
  }

  let loadedProjects: Project[] = []

  try {
    if (isOrganizations) {
      const loadedTeams = organizationsListSource === 'myAdminTeams'
        ? await fetchMyAdminTeamsByIds(ids)
        : await fetchOrganizationsByIdsFromGetList({
          organizationsGetListParams: organizationsGetListParams!,
          projectIds: ids,
        })
      loadedProjects = localizeTeamsAsProjects(loadedTeams, language)
    } else {
      const loaded = await fetchProjectsByIdsFromGetList({
        contextNames: contextNames || [],
        teamId,
        projectIds: ids,
      })
      loadedProjects = localizeProjects(loaded, language)
    }
  } catch {
    loadedProjects = []
  }

  const loadedIds = new Set(loadedProjects.map((item) => item.projectId))
  const missingIds = ids.filter((id) => !loadedIds.has(id))

  if (missingIds.length) {
    if (isOrganizations) {
      const fromGet = await fetchOrganizationsByIdsFromGet(missingIds)
      loadedProjects = [...loadedProjects, ...localizeTeamsAsProjects(fromGet, language)]
    } else {
      const fromGet = await fetchProjectsByIdsFromGet(missingIds)
      loadedProjects = [...loadedProjects, ...localizeProjects(fromGet, language)]
    }
  }

  return sortItemsByIds(
    excludeServiceOrganizationTeams(loadedProjects as TeamWithServiceFlag[]) as Project[],
    ids,
  )
}
